// src/routes/marketplace/marketplace-creator-routes.tsx
import { lazy } from 'react';
import type { RoutesProps } from '../routes';
import {
    FaChartBar,
    FaUsers,
    FaFileContract,
    FaDollarSign,
} from 'react-icons/fa';
import { BiStats } from 'react-icons/bi';
import { HiCollection, HiCash } from 'react-icons/hi';

// Creator components
const CreatorDashboard = lazy(() => import('@/pages/marketplace/CreatorDashboard'));
const ContentManagement = lazy(() => import('@/pages/marketplace/creator/ContentManagement'));
const CreatorAnalytics = lazy(() => import('@/pages/marketplace/creator/CreatorAnalytics'));
const IncomingRequests = lazy(() => import('@/pages/marketplace/creator/IncomingRequests'));
const EarningsWidget = lazy(() => import('@/pages/marketplace/creator/EarningsWidget'));
const PayoutManagement = lazy(() => import('@/pages/marketplace/creator/PayoutManagement'));

// Creator routes
export const marketplaceCreatorRoutes: RoutesProps[] = [
    {
        path: '/marketplace/creator',
        name: 'Creator Dashboard',
        element: <CreatorDashboard />,
        roles: ['right_owner'],
        icon: FaChartBar,
    },
    {
        path: '/marketplace/creator/content',
        name: 'Quản lý nội dung',
        element: <ContentManagement />,
        roles: ['right_owner'],
        icon: HiCollection,
    },
    {
        path: '/marketplace/creator/analytics',
        name: 'Phân tích doanh thu',
        element: <CreatorAnalytics />,
        roles: ['right_owner'],
        icon: BiStats,
    },
    {
        path: '/marketplace/creator/requests',
        name: 'Yêu cầu đến',
        element: <IncomingRequests />,
        roles: ['right_owner'],
        icon: FaFileContract,
    },
    {
        path: '/marketplace/creator/earnings',
        name: 'Thu nhập',
        element: <EarningsWidget />,
        roles: ['right_owner'],
        icon: FaDollarSign,
    },
    {
        path: '/marketplace/creator/payouts',
        name: 'Rút tiền',
        element: <PayoutManagement />,
        roles: ['right_owner'],
        icon: HiCash,
    },
];

// Navigation structure for creator tools
export const marketplaceCreatorNavigationStructure = {
    creatorDashboard: {
        key: 'creator-dashboard',
        translationKey: 'marketplace.creatorDashboard',
        path: '/marketplace/creator',
        icon: FaChartBar,
        roles: ['right_owner'],
    },
    contentManagement: {
        key: 'creator-content',
        translationKey: 'marketplace.creatorContent',
        path: '/marketplace/creator/content',
        icon: HiCollection,
        roles: ['right_owner'],
    },
    creatorAnalytics: {
        key: 'creator-analytics',
        translationKey: 'marketplace.creatorAnalytics',
        path: '/marketplace/creator/analytics',
        icon: BiStats,
        roles: ['right_owner'],
    },
    incomingRequests: {
        key: 'incoming-requests',
        translationKey: 'marketplace.incomingRequests',
        path: '/marketplace/creator/requests',
        icon: FaFileContract,
        roles: ['right_owner'],
    },
    earnings: {
        key: 'creator-earnings',
        translationKey: 'marketplace.earnings',
        path: '/marketplace/creator/earnings',
        icon: FaDollarSign,
        roles: ['right_owner'],
    },
    payouts: {
        key: 'creator-payouts',
        translationKey: 'marketplace.payouts',
        path: '/marketplace/creator/payouts',
        icon: HiCash,
        roles: ['right_owner'],
    },
};

// Helper functions
export const canAccessCreatorTools = (userRoles: string[]): boolean => {
    return userRoles.includes('right_owner');
};

export const getCreatorMenuGroup = (userRoles: string[]) => {
    if (!canAccessCreatorTools(userRoles)) return null;

    return {
        key: 'marketplace-creator-tools',
        translationKey: 'marketplace.creatorTools',
        icon: FaUsers,
        children: [
            marketplaceCreatorNavigationStructure.creatorDashboard,
            marketplaceCreatorNavigationStructure.contentManagement,
            marketplaceCreatorNavigationStructure.creatorAnalytics,
            marketplaceCreatorNavigationStructure.incomingRequests,
            marketplaceCreatorNavigationStructure.earnings,
            marketplaceCreatorNavigationStructure.payouts,
        ]
    };
};

export default marketplaceCreatorRoutes;